import axios from "axios";
import { BASE_URL } from "../constants";
import { refreshAccessToken } from "./auth";

const api = axios.create({ baseURL: BASE_URL });

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  for (const prom of failedQueue) {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  }
  
  failedQueue = [];
};

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalRequest = error.config;

    if (error?.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If we're already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err instanceof Error ? err : new Error(err));
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem("refreshToken");
      
      if (!refreshToken) {
        // No refresh token, redirect to login
        processQueue(error, null);
        // Dispatch logout action
        window.dispatchEvent(new CustomEvent('tokenExpired'));
        return Promise.reject(error instanceof Error ? error : new Error(error));
      }

      try {
        const response = await refreshAccessToken(refreshToken);
        
        if (response.status === 200) {
          const { token: newToken, refreshToken: newRefreshToken } = response;
          
          // Update tokens in localStorage
          localStorage.setItem("token", newToken);
          if (newRefreshToken) {
            localStorage.setItem("refreshToken", newRefreshToken);
          }
          
          // Dispatch event to update Redux store
          window.dispatchEvent(new CustomEvent('tokensRefreshed', {
            detail: { token: newToken, refreshToken: newRefreshToken }
          }));
          
          // Update the original request with new token
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          
          processQueue(null, newToken);
          isRefreshing = false;
          
          return api(originalRequest);
        } else {
          // Refresh failed, redirect to login
          processQueue(error, null);
          isRefreshing = false;
          window.dispatchEvent(new CustomEvent('tokenExpired'));
          return Promise.reject(error instanceof Error ? error : new Error(error));
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;
        window.dispatchEvent(new CustomEvent('tokenExpired'));
        return Promise.reject(refreshError instanceof Error ? refreshError : new Error(refreshError));
      }
    }

    return Promise.reject(error instanceof Error ? error : new Error(error));
  }
);

export default api;
